/**
 * CSTR turg'un rejim.
 *
 * ALGORITM
 *   1. τ = V/F, ΔT_ad, κ = UA/(F·ρ·cp).
 *   2. isothermal: T = Tin, X — moddiy balansdan (conversionAt), Newton bilan sayqal.
 *   3. adiabatic/jacketed: H(T) = 0 ildizlari qidiriladi:
 *        a) T ∈ [Tlo, Thi] (Tlo = min(Tin,Tj) + min(0,ΔT_ad), Thi = max(Tin,Tj) + max(0,ΔT_ad))
 *           oralig'i 400 nuqtaga bo'linib H(T) ishorasi o'zgargan joylar bisection bilan aniqlanadi;
 *        b) har ildiz (X(T), T) dan newtonSystem bilan sayqallanadi (o'lchamsiz qoldiqlar);
 *        c) qo'shimcha: "ehtiyotkor" boshlang'ich taxmin (X = 0.5, T = Tin) dan Newton;
 *        d) takrorlar birlashtiriladi, T bo'yicha saralanadi.
 *      Barcha yechimlar [Tlo, Thi] ichida: H(Tlo) ≥ 0, H(Thi) ≤ 0 (issiqlik balansi
 *      chegaralari), shu bois kamida bitta ildiz doim topiladi. Tegib o'tuvchi (ikkilangan)
 *      ildizlar ishora o'zgartirmaydi — bunday nozik holat 1-versiyada aniqlanmaydi.
 *   4. Asosiy chiqish — ENG PAST HARORATLI yechim (types.ts dagi izoh). Bir nechta bo'lsa
 *      `reactor.multipleSteadyStates` (info, count).
 *
 * ISSIQLIK BALANSI TEKSHIRUVI (test): F·ρ·cp·(Tin − T) + heatGenerated − heatRemoved = 0.
 *
 * MANBA: Levenspiel CRE 9-bob; Fogler ECRE 12-bob ("Multiple steady states").
 */
import { newtonSystem } from '../../solvers';
import { warn, type SolverDiagnostics, type SteadyResult, type Warning } from '../../types';
import { adiabaticRise, arrhenius, damkohler, mixtureProperties, reactionRate } from '../reaction';
import type { ReactionMixture } from '../reaction';
import {
  conversionAt,
  heatResidual,
  heatTransferUA,
  isStable,
  validateCstrInputs,
  validateCstrParams,
} from './balances';
import type {
  CstrInputs,
  CstrOutputs,
  CstrParams,
  CstrSteadyOutputs,
  SteadyStateInfo,
} from './types';

const SCAN_POINTS = 400;
const T_TOL_REL = 1e-6;
const X_TOL = 1e-7;

interface Root {
  X: number;
  T: number;
  diag: SolverDiagnostics;
}

/** newtonSystem bilan (X, T) sayqali; isothermal uchun faqat X. */
function polish(
  p: CstrParams,
  mix: ReactionMixture,
  tau: number,
  CA0: number,
  Tin: number,
  Tj: number,
  kappa: number,
  X0: number,
  T0: number,
): Root {
  const { k0, Ea } = p.reaction;
  const n = mix.order;
  const dTad = adiabaticRise(mix, CA0);
  const massRes = (X: number, T: number) => {
    const Da = damkohler(arrhenius(k0, Ea, T), tau, CA0, n);
    return X - Da * Math.pow(Math.max(0, 1 - X), n);
  };
  if (p.mode === 'isothermal') {
    const r = newtonSystem((x) => [massRes(x[0] as number, Tin)], [X0]);
    return { X: clamp01(r.value[0] as number), T: Tin, diag: toDiag(r) };
  }
  const r = newtonSystem(
    (x) => {
      const X = x[0] as number;
      const T = x[1] as number;
      return [massRes(X, T), heatResidual(T, X, Tin, dTad, kappa, Tj) / Tin];
    },
    [X0, T0],
  );
  return { X: clamp01(r.value[0] as number), T: r.value[1] as number, diag: toDiag(r) };
}

function toDiag(r: { converged: boolean; iterations: number; residual: number; message?: string }) {
  const d: SolverDiagnostics = {
    converged: r.converged,
    iterations: r.iterations,
    residual: r.residual,
  };
  if (r.message) d.message = r.message;
  return d;
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

/** Ishora o'zgarishi bo'yicha bisection (H monoton emas, shu bois lokal). */
function refineRoot(H: (T: number) => number, a: number, b: number): number {
  let lo = a;
  let hi = b;
  let flo = H(lo);
  for (let i = 0; i < 100; i++) {
    const mid = 0.5 * (lo + hi);
    const fm = H(mid);
    if (fm === 0 || hi - lo < 1e-10 * Math.max(1, mid)) return mid;
    if (flo * fm < 0) hi = mid;
    else {
      lo = mid;
      flo = fm;
    }
  }
  return 0.5 * (lo + hi);
}

function findRoots(
  p: CstrParams,
  mix: ReactionMixture,
  tau: number,
  CA0: number,
  Tin: number,
  Tj: number,
  kappa: number,
): Root[] {
  const { k0, Ea } = p.reaction;
  const n = mix.order;
  const dTad = adiabaticRise(mix, CA0);
  const roots: Root[] = [];
  const add = (r: Root) => {
    if (!Number.isFinite(r.T) || !Number.isFinite(r.X)) return;
    for (const q of roots) {
      if (Math.abs(q.T - r.T) < T_TOL_REL * Math.max(1, r.T) && Math.abs(q.X - r.X) < X_TOL) {
        if (!q.diag.converged && r.diag.converged) Object.assign(q, r);
        return;
      }
    }
    roots.push(r);
  };

  if (p.mode === 'isothermal') {
    add(polish(p, mix, tau, CA0, Tin, Tj, kappa, conversionAt(Tin, tau, CA0, k0, Ea, n), Tin));
    return roots;
  }

  const H = (T: number) =>
    heatResidual(T, conversionAt(T, tau, CA0, k0, Ea, n), Tin, dTad, kappa, Tj);
  const Tlo = Math.max(1, Math.min(Tin, Tj) + Math.min(0, dTad) - 1e-6);
  const Thi = Math.max(Tin, Tj) + Math.max(0, dTad) + 1e-6;
  let Tprev = Tlo;
  let Hprev = H(Tprev);
  for (let i = 1; i <= SCAN_POINTS; i++) {
    const T = Tlo + ((Thi - Tlo) * i) / SCAN_POINTS;
    const h = H(T);
    if (Hprev === 0) {
      add(
        polish(p, mix, tau, CA0, Tin, Tj, kappa, conversionAt(Tprev, tau, CA0, k0, Ea, n), Tprev),
      );
    } else if (Hprev * h < 0) {
      const Tr = refineRoot(H, Tprev, T);
      const Xr = conversionAt(Tr, tau, CA0, k0, Ea, n);
      const polished = polish(p, mix, tau, CA0, Tin, Tj, kappa, Xr, Tr);
      // Newton uzoqlashib ketsa bisection natijasi ishonchli
      const far = Math.abs(polished.T - Tr) > 0.5 * (T - Tprev) + 1e-6;
      add(
        far
          ? { X: Xr, T: Tr, diag: { converged: true, iterations: 0, residual: Math.abs(H(Tr)) } }
          : polished,
      );
    }
    Tprev = T;
    Hprev = h;
  }
  // Ehtiyotkor boshlang'ich taxmin (spetsifikatsiya): X = 0.5, T = Tin
  const cautious = polish(p, mix, tau, CA0, Tin, Tj, kappa, 0.5, Tin);
  if (cautious.diag.converged && cautious.T >= Tlo - 1e-6 && cautious.T <= Thi + 1e-6)
    add(cautious);
  roots.sort((a, b) => a.T - b.T);
  return roots;
}

/** Yechimdan chiqish maydonlarini hisoblash */
export function outputsFor(
  p: CstrParams,
  inputs: CstrInputs,
  mix: ReactionMixture,
  CA: number,
  T: number,
): CstrOutputs {
  const F = inputs.flowRate;
  const CA0 = inputs.CA0;
  const CB0 = inputs.CB0 ?? 0;
  const k = arrhenius(p.reaction.k0, p.reaction.Ea, T);
  const rate = reactionRate(k, CA, mix.order);
  const tau = F > 0 ? p.volume / F : Infinity;
  const UA = heatTransferUA(p);
  const heatGenerated = mix.heatRelease * p.volume * rate;
  let heatRemoved = 0;
  if (p.mode === 'jacketed') heatRemoved = UA * (T - (inputs.Tjacket as number));
  else if (p.mode === 'isothermal') heatRemoved = heatGenerated + F * mix.rhoCp * (inputs.Tin - T);
  return {
    conversion: CA0 > 0 ? 1 - CA / CA0 : 0,
    CA,
    CB: CB0 + mix.yieldB * (CA0 - CA),
    T,
    rate,
    k,
    residenceTime: tau,
    damkohler: damkohler(k, tau, CA0, mix.order),
    adiabaticRise: adiabaticRise(mix, CA0),
    heatGenerated,
    heatRemoved,
    density: mix.density,
    cp: mix.cp,
  };
}

export function solveSteady(p: CstrParams, inputs: CstrInputs): SteadyResult<CstrSteadyOutputs> {
  validateCstrParams(p);
  validateCstrInputs(p, inputs);
  const warnings: Warning[] = [];
  const mix = mixtureProperties(p.reaction, inputs.Tin, warnings);
  const { flowRate: F, CA0, Tin } = inputs;
  const Tj = p.mode === 'jacketed' ? (inputs.Tjacket as number) : Tin;

  // Sarf nol: reaktor "batch" kabi oxirigacha reaksiyaga kiradi (n > 0, k0 > 0)
  if (F <= 0) {
    warn(warnings, 'reactor.noFlow', 'critical');
    const complete = p.reaction.k0 > 0 && CA0 > 0;
    const CA = complete ? 0 : CA0;
    const dTad = adiabaticRise(mix, CA0);
    const T =
      p.mode === 'adiabatic' ? Tin + (complete ? dTad : 0) : p.mode === 'jacketed' ? Tj : Tin;
    const o = outputsFor(p, inputs, mix, CA, T);
    const ss: SteadyStateInfo = { conversion: o.conversion, CA, T, rate: o.rate, stable: true };
    return {
      outputs: { ...o, steadyStates: [ss] },
      warnings,
      diagnostics: { converged: true, iterations: 0, residual: 0 },
    };
  }

  const tau = p.volume / F;
  const kappa = heatTransferUA(p) / (F * mix.rhoCp);
  const roots = findRoots(p, mix, tau, CA0, Tin, Tj, kappa);
  const primary = roots[0] as Root;

  const steadyStates: SteadyStateInfo[] = roots.map((r) => {
    const CA = CA0 * (1 - r.X);
    const k = arrhenius(p.reaction.k0, p.reaction.Ea, r.T);
    return {
      conversion: r.X,
      CA,
      T: r.T,
      rate: reactionRate(k, CA, mix.order),
      stable: isStable(p, mix, F, CA, r.T),
    };
  });

  const out = outputsFor(p, inputs, mix, CA0 * (1 - primary.X), primary.T);
  if (roots.length > 1)
    warn(warnings, 'reactor.multipleSteadyStates', 'info', { count: roots.length });
  if (out.conversion < 1e-6) warn(warnings, 'reactor.noReaction', 'info');
  if (p.Tmax !== undefined && out.T > p.Tmax) {
    warn(warnings, 'reactor.runaway', 'critical', { T: Number(out.T.toFixed(1)), Tmax: p.Tmax });
  }

  return { outputs: { ...out, steadyStates }, warnings, diagnostics: primary.diag };
}
